import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import type { ImpersonationReason } from "@/services/platform-admin-api";

interface ImpersonationStartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
}

// Trigger-Klassifikation — Werte exakt aus startImpersonationSchema (Backend),
// deutsche Labels. Pflichtfeld; ein Freitext-Grund existiert backend-seitig
// (noch) nicht.
const REASON_OPTIONS: { value: ImpersonationReason; label: string }[] = [
  { value: "onboarding-support", label: "Onboarding-Support" },
  { value: "bug-investigation", label: "Fehleranalyse" },
  { value: "data-check", label: "Datenprüfung" },
  { value: "configuration-help", label: "Konfigurationshilfe" },
  { value: "other", label: "Sonstiges" },
];

export function ImpersonationStartDialog({
  open,
  onOpenChange,
  tenantId,
  tenantName,
}: ImpersonationStartDialogProps) {
  const { startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [reason, setReason] = useState<ImpersonationReason | "">("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open]);

  const submitDisabled = reason === "" || submitting;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitDisabled) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await startImpersonation(tenantId, reason as ImpersonationReason);
      onOpenChange(false);
      // Client-side in die Tenant-Sicht wechseln (kein Reload) — sonst bleibt
      // der Admin im Platform-Bereich trotz gesetztem Impersonation-Token.
      navigate("/dashboard");
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Impersonation fehlgeschlagen.",
      );
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Impersonation starten</DialogTitle>
            <DialogDescription>
              Du agierst anschließend als Benutzer des Tenants{" "}
              <span className="font-medium">{tenantName}</span>. Der Zugriff
              wird protokolliert (DSGVO Art. 28). Bitte wähle einen Grund.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 space-y-2">
            <Label htmlFor="impersonation-reason">Grund *</Label>
            <Select
              value={reason}
              onValueChange={(v) => setReason(v as ImpersonationReason)}
            >
              <SelectTrigger id="impersonation-reason">
                <SelectValue placeholder="Grund auswählen" />
              </SelectTrigger>
              <SelectContent>
                {REASON_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {submitError && (
              <p className="text-sm text-destructive">{submitError}</p>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={submitDisabled}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gestartet...
                </>
              ) : (
                "Impersonation starten"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
